/**
 * 🚨 CRITICAL SERVICE - DATABASE SERVICE
 *
 * Handles ALL database operations for the application.
 *
 * ⚠️ BEFORE MODIFYING:
 * 1. Read /docs/SYSTEMS/DATABASE.md
 * 2. Read /docs/ESSENTIAL/CRITICAL_SYSTEMS.md
 * 3. Check for components using this service (20+ imports)
 *
 * 🔒 CRITICAL RULES:
 * - NEVER change function signatures without checking ALL imports
 * - ALWAYS preserve exact Etsy data order (no artificial sorting)
 * - NEVER add artificial constraints (no Math.min/max on dimensions)
 * - ALWAYS use actual Etsy API data only
 *
 * 📍 CRITICAL FUNCTIONS:
 * - getUnifiedStickers(): Main data fetch (20+ components use this)
 * - getEtsyListingData(): Individual listing fetch (CoreStickerCard)
 * - getAllTags(): Tag data (NewHomePage, TagFilter)
 *
 * 🎯 SINGLE POINT OF FAILURE:
 * Breaking this service affects: NewHomePage, CoreStickerCard, HomePageSidebar,
 * AdminPanel, ListingAdmin, and 15+ other components.
 *
 * Last reviewed: 2025-01-15
 */

import logger from '../utils/logger.js';
import apiClient from './apiClient';

class DatabaseService {
  constructor() {
    // Use local database for development, direct API for production
    const hostname =
      (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    const port = (typeof window !== 'undefined' && window.location && window.location.port) || '';
    const search =
      (typeof window !== 'undefined' && window.location && window.location.search) || '';
    const forceLocal =
      /(?:[?&](?:api|local)=(?:local|1|true))(?:&|$)/i.test(search) ||
      (typeof window !== 'undefined' &&
        window.localStorage &&
        window.localStorage.getItem('USE_LOCAL_API') === '1');

    const isDevelopment =
      process.env.NODE_ENV === 'development' ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      port === '8888' ||
      forceLocal;

    if (isDevelopment) {
      this.baseUrl = '/.netlify/functions/local-db';
      this.unifiedBaseUrl = '/.netlify/functions';
    } else {
      // Keep fields for backward compatibility; client handles ?path=
      this.baseUrl = 'https://www.clothedeye.com/api/index.php';
      this.unifiedBaseUrl = 'https://www.clothedeye.com/api/index.php';
    }
    try {
      logger.log(`🔌 API base configured: ${this.unifiedBaseUrl} (dev=${isDevelopment})`);
    } catch (_) {}
  }

  /**
   * Get all stickers from the database
   */
  async getAllStickers() {
    try {
      const response = await fetch(`${this.baseUrl}/stickers`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching stickers:', error);
      return [];
    }
  }

  /**
   * Get a specific sticker by Etsy listing ID (UNIFIED DATA)
   */
  async getStickerByListingId(listingId) {
    try {
      logger.log(`🌐 Fetching UNIFIED sticker data for: ${listingId}`);
      const response = await fetch(`${this.unifiedBaseUrl}/unified-stickers/${listingId}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const sticker = data.data || null;

      // Validate individual sticker has price data
      if (sticker) {
        const validatedStickers = this.validateStickerPrices(
          [sticker],
          `getStickerByListingId(${listingId})`
        );
        return validatedStickers.length > 0 ? validatedStickers[0] : null;
      }

      return sticker;
    } catch (error) {
      console.error(`Error fetching unified sticker ${listingId}:`, error);
      return null;
    }
  }

  /**
   * Get complete listing data by listing ID (UNIFIED DATA)
   */
  async getEtsyListingData(listingId, bypassCache = false) {
    try {
      // Fetching UNIFIED listing data
      const url = `${this.unifiedBaseUrl}/unified-stickers/${listingId}`;
      const cacheBuster = bypassCache ? `?t=${Date.now()}` : '';
      const response = await fetch(`${url}${cacheBuster}`);
      // Response received

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Response data received
      return data.data || null;
    } catch (error) {
      console.error(`❌ Error fetching UNIFIED listing data ${listingId}:`, error);
      return null;
    }
  }

  /**
   * Get all Etsy listing IDs from database
   */
  async getAllEtsyListingIds() {
    try {
      const response = await fetch(`${this.baseUrl}/etsy-listing-ids`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching Etsy listing IDs:', error);
      return [];
    }
  }

  /**
   * Get all listings from database (UNIFIED DATA)
   */
  async getAllListings() {
    try {
      logger.log(`🌐 Fetching all UNIFIED listings...`);
      const response = await fetch(`${this.unifiedBaseUrl}/unified-stickers`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching all UNIFIED listings:', error);
      return [];
    }
  }

  /**
   * Get all unique tags from database
   */
  async getAllTags() {
    try {
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = Date.now();
      const data = await apiClient.requestJson({ path: '/global-tags', params: { t: timestamp } });
      return data.data || [];
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  }

  /**
   * Create a new sticker
   */
  async createSticker(stickerData) {
    try {
      const response = await fetch(`${this.baseUrl}/stickers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stickerData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error creating sticker:', error);
      return null;
    }
  }

  /**
   * Update an existing sticker (UNIFIED DATA)
   */
  async updateSticker(listingId, stickerData) {
    try {
      logger.log(`🌐 Updating UNIFIED sticker data for: ${listingId}`);
      const response = await fetch(`${this.unifiedBaseUrl}/unified-stickers/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stickerData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error Response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      logger.log(`🔍 API Response for sticker ${listingId}:`, data);
      return data.data || data || { success: true };
    } catch (error) {
      console.error(`Error updating UNIFIED sticker ${listingId}:`, error);
      return null;
    }
  }

  /**
   * Delete a sticker (UNIFIED DATA)
   */
  async deleteSticker(listingId) {
    try {
      logger.log(`🌐 Deleting UNIFIED sticker data for: ${listingId}`);
      const response = await fetch(`${this.unifiedBaseUrl}/unified-stickers/${listingId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error(`Error deleting UNIFIED sticker ${listingId}:`, error);
      return false;
    }
  }

  /**
   * Create or update listing data (UNIFIED DATA)
   */
  async upsertEtsyListingData(listingId, listingData) {
    try {
      logger.log(`🌐 Upserting UNIFIED listing data for: ${listingId}`);
      const response = await fetch(`${this.unifiedBaseUrl}/unified-stickers/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(listingData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error(`Error upserting UNIFIED sticker ${listingId}:`, error);
      return null;
    }
  }

  /**
   * Get lightweight data for SimpleStickerCard display
   * Returns only essential data needed for grid view
   */
  async getLightweightListingData(listingId) {
    try {
      logger.log(`🌐 Fetching UNIFIED data for: ${listingId}`);
      const response = await fetch(`${this.unifiedBaseUrl}/unified-stickers/${listingId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const dbData = data.data;

      if (!dbData) {
        return null;
      }

      // UNIFIED DATA: All fields are already processed and ready to use
      logger.log('✅ Using pre-processed unified data');

      // Data is already parsed and ready from unified table
      const sizes = dbData.available_sizes || [];
      const colors = dbData.available_colors || [];

      // Sizes are already sorted in the unified table
      const sortedSizes = sizes;

      // Image URLs are pre-computed in unified table
      const largestSizeImageUrl = dbData.largest_size_image_url || null;
      const firstImageUrl = dbData.first_image_url || null;

      return {
        title: dbData.title || '',
        category: dbData.category || '',
        firstImageUrl,
        largestSizeImageUrl,
        listingId,
        etsyUrl: dbData.etsyUrl || '',
        availableSizes: sortedSizes,
        availableColors: colors,
        images: dbData.all_image_urls || [],
        variationImages: dbData.variation_images_data || { results: [] },
        listingData: {
          title: dbData.title || '',
          description: dbData.description || '',
          state: dbData.state || '',
          views: dbData.views || 0,
          num_favorers: dbData.num_favorers || 0,
          featured_rank: dbData.featured_rank || null,
        },
        inventory: dbData.inventory_matrix || {},
      };
    } catch (error) {
      console.error(`❌ Error fetching lightweight data for ${listingId}:`, error);
      return null;
    }
  }

  /**
   * Get unified sticker data (new unified table)
   */
  async getUnifiedStickerData(listingId) {
    try {
      logger.log(`🌐 Fetching unified data for: ${listingId}`);
      const isDev =
        process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
      const endpoint = isDev
        ? `${this.unifiedBaseUrl}/unified-stickers/${listingId}`
        : `${this.unifiedBaseUrl}/unified-stickers/${listingId}`;

      const response = await fetch(endpoint);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`❌ Error fetching unified data for ${listingId}:`, error);
      return null;
    }
  }

  /**
   * Validate that sticker data has required price information
   */
  validateStickerPrices(stickers, source = 'unknown') {
    if (!Array.isArray(stickers)) {
      console.warn(`⚠️ PRICE VALIDATION WARNING: ${source} - stickers is not an array:`, stickers);
      return [];
    }

    const validStickers = [];
    const stickersWithMissingPrices = [];

    stickers.forEach((sticker, _index) => {
      if (!sticker.price || !sticker.price.amount || !sticker.price.formatted) {
        // Add fallback "See Etsy" data for stickers with missing prices
        const fallbackSticker = {
          ...sticker,
          price: {
            amount: null,
            currency: 'USD',
            formatted: null,
            source: 'etsy_fallback',
          },
          hasPriceFallback: true,
          originalPriceData: sticker.price,
        };
        stickersWithMissingPrices.push(fallbackSticker);

        // Only log in development, not production
        if (process.env.NODE_ENV === 'development') {
          console.warn(`⚠️ FALLBACK PRICE for listing ${sticker.etsyListingId}: ${sticker.title}`);
          console.warn(`   Using "See Etsy" fallback - price data missing`);
        }
      } else {
        validStickers.push(sticker);
      }
    });

    // Combine valid stickers with fallback stickers
    const allStickers = [...validStickers, ...stickersWithMissingPrices];

    if (stickersWithMissingPrices.length > 0) {
      console.warn(`⚠️ PRICE FALLBACK ACTIVATED from ${source}:`);
      console.warn(
        `   ${stickersWithMissingPrices.length} out of ${stickers.length} stickers using "See Etsy" fallback`
      );
      console.warn(`   Total stickers available: ${allStickers.length}`);

      // Only log in development to avoid production noise
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `   Stickers with fallbacks:`,
          stickersWithMissingPrices.map((s) => s.etsyListingId)
        );
      }
    }

    return allStickers;
  }

  /**
   * Get all unified stickers with filtering and sales-based sorting
   */
  async getUnifiedStickers(options = {}) {
    try {
      const params = { ...options, _t: Date.now() };
      const data = await apiClient.requestJson({ path: '/unified-stickers', params });
      const validatedStickers = this.validateStickerPrices(
        data.data || [],
        'getUnifiedStickers(/unified-stickers)'
      );
      return { ...data, data: validatedStickers };
    } catch (error) {
      console.error(`❌ Error fetching unified stickers:`, error);
      return { data: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
    }
  }

  /**
   * Get ALL stickers without pagination limits - loads everything
   */
  async getAllUnifiedStickers(options = {}) {
    try {
      // logger.log('🚀 Loading ALL stickers with high limit...');

      // Request all stickers with a very high limit
      const result = await this.getUnifiedStickers({
        ...options,
        page: 1,
        limit: 999, // This will trigger unlimited mode in backend
      });

      // Additional validation for the "get all" method
      const validatedStickers = this.validateStickerPrices(
        result.data || [],
        'getAllUnifiedStickers()'
      );

      // logger.log(`✅ Loaded ${validatedStickers.length} total stickers with price validation`);
      return {
        data: validatedStickers,
        total: validatedStickers.length,
      };
    } catch (error) {
      console.error(`❌ Error fetching ALL unified stickers:`, error);
      return { data: [], total: 0 };
    }
  }

  /**
   * Get svgPath-only stickers (optimized for hero scene and parallax)
   * Returns only stickers with svgPath defined (~50-80 vs 312 items)
   * Uses server-side caching for improved performance
   */
  async getSvgPathStickers() {
    try {
      const data = await apiClient.requestJson({ path: '/svgpath-stickers' });
      const validatedStickers = this.validateStickerPrices(data.data || [], 'getSvgPathStickers()');
      return { data: validatedStickers, total: validatedStickers.length };
    } catch (error) {
      console.error(`❌ Error fetching svgPath stickers:`, error);
      return { data: [], total: 0 };
    }
  }

  /**
   * Update unified sticker data
   */
  async updateUnifiedSticker(listingId, updateData) {
    try {
      logger.log(`🔄 Updating unified sticker: ${listingId}`);

      const response = await fetch(`${this.baseUrl}/unified-stickers/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Update failed: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      logger.log(`✅ Successfully updated unified sticker: ${listingId}`);
      return result;
    } catch (error) {
      console.error(`❌ Error updating unified sticker ${listingId}:`, error);
      throw error;
    }
  }

  /**
   * Create new unified sticker
   */
  async createUnifiedSticker(stickerData) {
    try {
      logger.log(`🔄 Creating unified sticker: ${stickerData.etsyListingId}`);

      const response = await fetch(`${this.unifiedBaseUrl}/unified-stickers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stickerData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Create failed: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      logger.log(`✅ Successfully created unified sticker: ${stickerData.etsyListingId}`);
      return result;
    } catch (error) {
      console.error(`❌ Error creating unified sticker:`, error);
      throw error;
    }
  }

  /**
   * Get paginated listings with lightweight data
   */
  async getPaginatedListings(
    page = 1,
    limit = 50,
    search = '',
    sortBy = 'title',
    sortOrder = 'asc',
    category = '',
    hasSvgPath = false
  ) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
        ...(search && { search }),
        ...(category && { category }),
        ...(hasSvgPath && { hasSvgPath: 'true' }),
      });

      const response = await fetch(`${this.baseUrl}/listings-paginated?${params}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result; // Return the full response with data and pagination
    } catch (error) {
      console.error('Error fetching paginated listings:', error);
      return { data: [], pagination: {} };
    }
  }

  /**
   * Test database connection
   */
  async testConnection() {
    try {
      await apiClient.requestJson({ path: '/health' });
      return true;
    } catch (error) {
      console.error('Database connection test failed:', error);
      return false;
    }
  }

  /**
   * Get Etsy listing properties (colors, sizes, etc.)
   */
  async getEtsyListingProperties(shopId, listingId, authToken) {
    try {
      const response = await fetch(
        `/.netlify/functions/etsy-get-properties/${shopId}/${listingId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error getting Etsy listing properties:`, error);
      return null;
    }
  }

  /**
   * Map ClothedEye color names to Etsy format
   */
  mapColorToEtsyFormat(clothedEyeColor) {
    const colorMap = {
      BLACK: 'Black - Matte',
      WHITE: 'White - Matte',
      GRAY: 'Gray',
      RED: 'Red',
      ORANGE: 'Orange',
      YELLOW: 'Yellow',
      DOZER_YELLOW: 'Dozer Yellow',
      GREEN: 'Green',
      FOREST_GREEN: 'Forest Green',
      ICE_BLUE: 'Ice Blue',
      SKY_BLUE: 'Sky Blue',
      BLUE: 'Blue',
      PURPLE: 'Purple',
      VIOLET: 'Violet',
      PINK: 'Pink',
      SILVER_METALLIC: 'Silver Metallic',
      GOLD_METALLIC: 'Gold Metallic',
      COPPER_METALLIC: 'Copper Metallic',
      GRAY_HOLOGRAPHIC: 'Gray Holographic',
      NUT_BROWN: 'Nut Brown',
      BROWN: 'Brown',
    };
    return colorMap[clothedEyeColor] || clothedEyeColor;
  }

  /**
   * Map multiple ClothedEye color names to Etsy format
   */
  mapColorsToEtsyFormat(clothedEyeColors) {
    return clothedEyeColors.map((color) => this.mapColorToEtsyFormat(color));
  }

  /**
   * Discover the color property for a listing
   */
  async discoverColorProperty(shopId, listingId, authToken) {
    try {
      logger.log(`🔍 Discovering color property for shop ${shopId}, listing ${listingId}`);
      const propertiesData = await this.getEtsyListingProperties(shopId, listingId, authToken);

      if (!propertiesData) {
        console.error('❌ No properties data found');
        return null;
      }

      logger.log('📊 Raw properties data:', propertiesData);

      // Look for the color property in the actual data
      if (propertiesData.results && propertiesData.results.length > 0) {
        logger.log('🔍 Available properties:');
        propertiesData.results.forEach((property, index) => {
          logger.log(`  ${index + 1}. ${property.property_name} (ID: ${property.property_id})`);
        });

        for (const property of propertiesData.results) {
          logger.log('🔍 Checking property:', property);
          if (
            property.property_name &&
            (property.property_name.toLowerCase().includes('color') ||
              property.property_name.toLowerCase().includes('colour'))
          ) {
            logger.log('✅ Found color property:', property);
            return {
              property_id: property.property_id,
              property_name: property.property_name,
              value_ids: property.value_ids || [],
            };
          }
        }
      } else {
        logger.log('📋 No properties found in listing data');
        logger.log(
          '💡 This listing has no properties configured. Properties are tied to taxonomy nodes.'
        );
        logger.log('💡 We need to either:');
        logger.log('   1. Assign the listing to a taxonomy that supports color properties');
        logger.log('   2. Use inventory variations instead of listing properties');
      }

      logger.log('❌ No color property found in listing properties');
      return null;
    } catch (error) {
      console.error('❌ Error discovering color property:', error);
      return null;
    }
  }

  /**
   * Update listing colors via property endpoint
   */
  async updateListingColors(shopId, listingId, clothedEyeColors, authToken) {
    try {
      logger.log('🎨 Updating colors via property endpoint...');

      // Map colors to Etsy format
      const etsyColors = this.mapColorsToEtsyFormat(clothedEyeColors);

      // Update property values via Etsy API
      const response = await fetch(`/.netlify/functions/etsy-update-property-values`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          shopId,
          listingId,
          colors: etsyColors,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      logger.log('✅ Updated property colors:', data);
      return data;
    } catch (error) {
      console.error('❌ Error updating property colors:', error);
      throw error;
    }
  }

  /**
   * Update Etsy listing properties (colors, sizes, etc.)
   */
  async updateEtsyListingProperties(shopId, listingId, propertyId, valueIds, values, authToken) {
    try {
      const response = await fetch(`/.netlify/functions/etsy-update-properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          shopId,
          listingId,
          propertyId,
          valueIds,
          values,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error updating Etsy listing properties:`, error);
      return null;
    }
  }

  /**
   * Update Etsy listing details (title, description, etc.)
   */
  async updateEtsyListing(listingId, shopId, updateData, authToken) {
    try {
      const response = await fetch(`/.netlify/functions/etsy-update-listing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          listingId,
          shopId,
          ...updateData,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error updating Etsy listing:`, error);
      return null;
    }
  }

  /**
   * Save color audit progress to database
   */
  async saveColorAuditProgress(auditResults) {
    try {
      const response = await fetch(`${this.baseUrl}/color-audit-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auditResults,
          timestamp: new Date().toISOString(),
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error saving color audit progress:', error);
      return null;
    }
  }

  /**
   * Get color audit progress from database
   */
  async getColorAuditProgress() {
    try {
      const response = await fetch(`${this.baseUrl}/color-audit-progress`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error getting color audit progress:', error);
      return null;
    }
  }

  /**
   * Clear color audit progress from database
   */
  async clearColorAuditProgress() {
    try {
      const response = await fetch(`${this.baseUrl}/color-audit-progress`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error clearing color audit progress:', error);
      return null;
    }
  }

  /**
   * Get auto-association progress from database
   */
  async getAutoAssociationProgress() {
    try {
      const isDev =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');
      const endpoint = isDev
        ? 'http://localhost:8888/.netlify/functions/auto-association-progress'
        : `${this.baseUrl}/auto-association-progress`;
      const response = await fetch(endpoint);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update auto-association progress in database
   */
  async updateAutoAssociationProgress(progressData) {
    try {
      const isDev =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');
      const endpoint = isDev
        ? 'http://localhost:8888/.netlify/functions/auto-association-progress'
        : `${this.baseUrl}/auto-association-progress`;
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(progressData),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * RECOMMENDATION SYSTEM METHODS
   * Methods for managing cross-sell/upsell recommendations
   */

  /**
   * Get recommendations for a specific listing
   */
  async getRecommendationsForListing(listingId) {
    try {
      const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');

      const baseUrl = isDevelopment
        ? 'http://localhost:8888/.netlify/functions'
        : 'https://www.clothedeye.com/api';

      const response = await fetch(`${baseUrl}/listing-recommendations/${listingId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error(`Error fetching recommendations for listing ${listingId}:`, error);
      return [];
    }
  }

  /**
   * Create a new recommendation
   */
  async createRecommendation(recommendationData) {
    try {
      const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');

      const baseUrl = isDevelopment
        ? 'http://localhost:8888/.netlify/functions'
        : 'https://www.clothedeye.com/api';

      const response = await fetch(`${baseUrl}/listing-recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(recommendationData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error('Error creating recommendation:', error);
      return null;
    }
  }

  /**
   * Update an existing recommendation
   */
  async updateRecommendation(recommendationId, updateData) {
    try {
      const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');

      const baseUrl = isDevelopment
        ? 'http://localhost:8888/.netlify/functions'
        : 'https://www.clothedeye.com/api';

      const response = await fetch(`${baseUrl}/listing-recommendations/${recommendationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error(`Error updating recommendation ${recommendationId}:`, error);
      return null;
    }
  }

  /**
   * Delete a recommendation
   */
  async deleteRecommendation(recommendationId) {
    try {
      const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');

      const baseUrl = isDevelopment
        ? 'http://localhost:8888/.netlify/functions'
        : 'https://www.clothedeye.com/api';

      const response = await fetch(`${baseUrl}/listing-recommendations/${recommendationId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success || false;
    } catch (error) {
      console.error(`Error deleting recommendation ${recommendationId}:`, error);
      return false;
    }
  }

  /**
   * Delete recommendation by source listing and position
   */
  async deleteRecommendationByPosition(sourceListingId, position) {
    try {
      const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');

      const baseUrl = isDevelopment
        ? 'http://localhost:8888/.netlify/functions'
        : 'https://www.clothedeye.com/api';

      const response = await fetch(
        `${baseUrl}/listing-recommendations/delete?source_listing_id=${sourceListingId}&position=${position}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success || false;
    } catch (error) {
      console.error(
        `Error deleting recommendation for ${sourceListingId} position ${position}:`,
        error
      );
      return false;
    }
  }

  /**
   * Get all recommendations (admin use)
   */
  async getAllRecommendations() {
    try {
      const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.hostname === 'localhost');

      const baseUrl = isDevelopment
        ? 'http://localhost:8888/.netlify/functions'
        : 'https://www.clothedeye.com/api';

      const response = await fetch(`${baseUrl}/listing-recommendations`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching all recommendations:', error);
      return [];
    }
  }

  /**
   * Track recommendation interaction (click/view)
   */
  async trackRecommendationInteraction(recommendationId, interactionType = 'click') {
    try {
      const updateData = {};
      if (interactionType === 'click') {
        updateData.click_count = 1; // Will be incremented in the backend
      } else if (interactionType === 'view') {
        updateData.view_count = 1; // Will be incremented in the backend
      }

      return await this.updateRecommendation(recommendationId, updateData);
    } catch (error) {
      console.error(
        `Error tracking ${interactionType} for recommendation ${recommendationId}:`,
        error
      );
      return false;
    }
  }

  /**
   * REVIEW SYSTEM METHODS
   * Methods for managing customer reviews with images
   */

  /**
   * Store reviews in the database
   * @param {Array} reviews - Array of review objects from Etsy
   * @returns {Promise<Object>} Result with import statistics
   */
  async storeReviews(reviews) {
    try {
      const response = await fetch(`${this.baseUrl}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reviews }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to store reviews');
      }

      return await response.json();
    } catch (error) {
      console.error('Error storing reviews:', error);
      throw error;
    }
  }

  /**
   * Get all reviews with images (5-star only)
   * @param {number} limit - Optional limit for number of reviews to return
   * @returns {Promise<Array>} Array of review objects
   */
  async getAllReviewsWithImages(limit = null) {
    try {
      const url = limit ? `${this.baseUrl}/reviews?limit=${limit}` : `${this.baseUrl}/reviews`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Error fetching reviews:', error);
      return [];
    }
  }

  /**
   * Get reviews for a specific listing
   * @param {string} listingId - The Etsy listing ID
   * @returns {Promise<Array>} Array of review objects for the listing
   */
  async getReviewsByListingId(listingId) {
    try {
      const response = await fetch(`${this.baseUrl}/reviews/${listingId}`);

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error(`Error fetching reviews for listing ${listingId}:`, error);
      return [];
    }
  }

  /**
   * Clear all reviews (for testing/reset)
   * @returns {Promise<Object>} Result with deletion count
   */
  async clearAllReviews() {
    try {
      const response = await fetch(`${this.baseUrl}/reviews`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error clearing reviews:', error);
      throw error;
    }
  }

  /**
   * HOMEPAGE CONFIGURATION METHODS
   * Methods for managing homepage settings like priority tags
   */

  /**
   * Get homepage configuration by key
   * @param {string} key - Configuration key (e.g., 'priority_tags')
   * @returns {Promise<any>} Configuration value
   */
  async getHomepageConfig(key) {
    try {
      const response = await fetch(`${this.baseUrl}/homepage-config?key=${key}`);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.value || null;
    } catch (error) {
      console.error(`Error fetching homepage config ${key}:`, error);
      return null;
    }
  }

  /**
   * Get all homepage configurations
   * @returns {Promise<Object>} Object with all configurations
   */
  async getAllHomepageConfigs() {
    try {
      const response = await fetch(`${this.baseUrl}/homepage-config`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || {};
    } catch (error) {
      console.error('Error fetching all homepage configs:', error);
      return {};
    }
  }

  /**
   * Save homepage configuration
   * @param {string} key - Configuration key
   * @param {any} value - Configuration value (will be JSON stringified)
   * @returns {Promise<Object>} Result with success status
   */
  async saveHomepageConfig(key, value) {
    try {
      const response = await fetch(`${this.baseUrl}/homepage-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, value }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save homepage config');
      }

      return await response.json();
    } catch (error) {
      console.error(`Error saving homepage config ${key}:`, error);
      throw error;
    }
  }

  /**
   * Delete homepage configuration
   * @param {string} key - Configuration key to delete
   * @returns {Promise<boolean>} Success status
   */
  async deleteHomepageConfig(key) {
    try {
      const response = await fetch(`${this.baseUrl}/homepage-config`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error(`Error deleting homepage config ${key}:`, error);
      return false;
    }
  }
}

// Create singleton instance
const databaseService = new DatabaseService();

export default databaseService;
